// @ds/forms/TimePickerField
//
// Forms tier · Fast-typing text input for time values. Accepts loose formats and
// normalises intelligently on blur or Enter. Replaces the AntD column-spinner
// TimePicker which required many clicks to fill.
//
// Parsing rules (see TimePickerField.md for the full matrix):
//   "1430"     → "14:30"   "9"      → "09:00"
//   "14:30"    → "14:30"   "9.30"   → "09:30"
//   "14"       → "14:00"   "9 30"   → "09:30"
//   "2330"     → "23:30"   "25:00"  → null (invalid)
//   ""         → null
// When includeSeconds: also "143000" → "14:30:00", "14:30:00" → "14:30:00".
//
// onChange fires on blur / Enter / preset chip click — NEVER on every keystroke.
//
// All visual values come from @ds/tokens/palette.

import React, { useState, useEffect, type KeyboardEvent } from "react";
import { Input } from "antd";
import { parseTimeInput, toDisplayString } from "@ds/forms/TimePickerField.helpers";
import type { SizeType } from "antd/es/config-provider/SizeContext";
import type { Dayjs } from "dayjs";
import { Pill } from "@ds/display/Pill";
import { Inline } from "@ds/layout/Inline";
import { Stack } from "@ds/layout/Stack";

// Native button reset for preset chips — strips browser chrome so the inner
// Pill paints flush. Injected once per page; uses class hook only, no inline
// `style` on the raw <button>.
const PRESET_RESET_CSS_ID = "__ds-time-preset-reset";
function ensurePresetResetCSS(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(PRESET_RESET_CSS_ID)) return;
  const el = document.createElement("style");
  el.id = PRESET_RESET_CSS_ID;
  el.textContent = `.ds-time-preset-reset { background: transparent; border: 0; padding: 0; cursor: pointer; font: inherit; }
.ds-time-preset-reset:disabled { cursor: not-allowed; opacity: 0.55; }`;
  document.head.appendChild(el);
}

// ─── Inputs ───────────────────────────────────────────────────────────────────

export interface TimePickerFieldInput {
  /** Time as a HH:mm or HH:mm:ss string (Supabase format) or a Dayjs instance. */
  value?: string | Dayjs;
  /** When true, emits HH:mm:ss; otherwise HH:mm. Default false. */
  includeSeconds?: boolean;
  /**
   * Optional list of preset time strings rendered as clickable Pill chips below
   * the input (e.g. `["06:00", "09:00", "12:00"]`). Click a chip → sets value
   * immediately. Default undefined (no chips rendered).
   */
  presets?: string[];
  /** `data-testid` applied to the root wrapper div. */
  testId?: string;
  /** Disables the input. */
  disabled?: boolean;
  /**
   * Placeholder text. Defaults to "HH:mm" or "HH:mm:ss" when includeSeconds.
   */
  placeholder?: string;
  /**
   * AntD Input status prop — passed through as-is.
   * `""` = normal, `"warning"` = amber ring, `"error"` = red ring.
   */
  status?: "" | "warning" | "error";
  /** AntD Input size — "small" | "middle" | "large". */
  size?: SizeType;
  /** Autofocus the input on mount. */
  autoFocus?: boolean;
  /**
   * Additional keyDown handler on the inner input. Called AFTER the
   * built-in Enter-to-commit logic — useful for consumers that need to
   * intercept other keys (e.g. Escape to cancel an editable cell).
   */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
}

// ─── Outputs ──────────────────────────────────────────────────────────────────

export interface TimePickerFieldOutput {
  /**
   * Fires on blur, Enter key, or preset chip click. Value is a canonical
   * "HH:mm" (or "HH:mm:ss") string, or null for empty / invalid input.
   * Does NOT fire on every keystroke.
   */
  onChange?: (value: string | null) => void;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export type TimePickerFieldProps = TimePickerFieldInput & TimePickerFieldOutput;

// ─── Component ────────────────────────────────────────────────────────────────

export const TimePickerField: React.FC<TimePickerFieldProps> = ({
  value,
  onChange,
  includeSeconds = false,
  presets,
  testId,
  disabled = false,
  placeholder,
  status,
  size,
  autoFocus,
  onKeyDown: externalKeyDown,
}) => {
  const defaultPlaceholder = includeSeconds ? "HH:mm:ss" : "HH:mm";

  // Local display state — typed string the user sees.
  const [displayValue, setDisplayValue] = useState<string>(() =>
    toDisplayString(value, includeSeconds),
  );

  // Keep display in sync when the controlled `value` prop changes externally.
  useEffect(() => {
    setDisplayValue(toDisplayString(value, includeSeconds));
  }, [value, includeSeconds]);

  // ── Commit handler (fires on blur / Enter) ─────────────────────────────
  const commit = (raw: string) => {
    const parsed = parseTimeInput(raw, includeSeconds);
    if (parsed !== null) {
      // Valid — normalise display and fire.
      setDisplayValue(parsed);
      onChange?.(parsed);
    } else if (raw.trim() === "") {
      // Empty — fire null.
      setDisplayValue("");
      onChange?.(null);
    } else {
      // Invalid input — revert to last valid (or empty if none).
      const reverted = toDisplayString(value, includeSeconds);
      setDisplayValue(reverted);
      // Don't fire onChange — value hasn't changed.
    }
  };

  const handleBlur = () => commit(displayValue);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit(displayValue);
      (e.target as HTMLInputElement).blur();
    }
    externalKeyDown?.(e);
  };

  // ── Preset chip click ──────────────────────────────────────────────────
  const handlePreset = (preset: string) => {
    if (disabled) return;
    setDisplayValue(preset);
    onChange?.(preset);
  };

  ensurePresetResetCSS();

  return (
    <Stack testId={testId} gap="xs" fullWidth>
      <Input
        value={displayValue}
        onChange={(e) => setDisplayValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder ?? defaultPlaceholder}
        status={status}
        size={size}
        autoFocus={autoFocus}
        inputMode="numeric"
        // AntD pattern: width:100% on AntD input is the canonical full-width hook.
        style={{ width: "100%" }}
        // Inner input testid for direct access in specs.
        data-testid={testId ? `${testId}-input` : undefined}
      />

      {presets != null && presets.length > 0 && (
        <Inline wrap gap="xs">
          {presets.map((preset) => (
            // Native <button> retains `disabled` attribute + keyboard semantics.
            // Pill owns the visual chrome; the button is a styleless reset
            // wrapper (no `style` attr — flagged-and-fixed at migration).
            <button
              key={preset}
              type="button"
              className="ds-time-preset-reset"
              disabled={disabled}
              onClick={() => handlePreset(preset)}
              data-testid={testId ? `${testId}-preset-${preset}` : undefined}
            >
              <Pill tone="grey" size="sm">
                {preset}
              </Pill>
            </button>
          ))}
        </Inline>
      )}
    </Stack>
  );
};
